import { useCallback, useEffect, useRef, useState } from 'react'
import { getInstagramReel } from '@domain/features/instagram-reels/instagram-reels-feature'
import type { InstagramReel } from '@domain/models/instagram-reel'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import {
  detailEntityMatchesRoute,
  isDetailLoadErrorFatal,
  keepDetailEntityForRoute,
} from './detail-load-state'

function matchesInstagramReelRoute(reel: InstagramReel, reelId: string): boolean {
  return reel.reelId === reelId || reel.uid === reelId
}

export function useInstagramReel(reelId: string | undefined) {
  const ssrReel = useSsrPageData()?.instagramReelDetail?.reel ?? null
  const hasMatchingSsrReel = detailEntityMatchesRoute(
    ssrReel,
    reelId,
    matchesInstagramReelRoute,
  )
  const [reel, setReelState] = useState<InstagramReel | null>(() =>
    keepDetailEntityForRoute(ssrReel, reelId, matchesInstagramReelRoute))
  const [loading, setLoading] = useState(!hasMatchingSsrReel)
  const [error, setError] = useState<string | null>(null)
  const reelRef = useRef<InstagramReel | null>(reel)

  const setReel = useCallback((nextReel: InstagramReel | null) => {
    reelRef.current = nextReel
    setReelState(nextReel)
  }, [])

  const load = useCallback(async () => {
    await Promise.resolve()

    if (!reelId) {
      setReel(null)
      setLoading(false)
      setError('Instagram content not found')
      return
    }

    const resolvedReelId = reelId
    const ssrFallback = keepDetailEntityForRoute(
      ssrReel,
      resolvedReelId,
      matchesInstagramReelRoute,
    )
    if (ssrFallback && !detailEntityMatchesRoute(
      reelRef.current,
      resolvedReelId,
      matchesInstagramReelRoute,
    )) {
      setReel(ssrFallback)
    } else if (!detailEntityMatchesRoute(
      reelRef.current,
      resolvedReelId,
      matchesInstagramReelRoute,
    )) {
      setReel(null)
    }

    try {
      if (!detailEntityMatchesRoute(
        reelRef.current,
        resolvedReelId,
        matchesInstagramReelRoute,
      )) {
        setLoading(true)
      }
      setError(null)
      const result = await getInstagramReel(resolvedReelId)
      setReel(result)
    } catch (loadError) {
      if (isDetailLoadErrorFatal(
        reelRef.current,
        resolvedReelId,
        matchesInstagramReelRoute,
      )) {
        setError(
          loadError instanceof Error ? loadError.message : 'Failed to load Instagram content.',
        )
      }
    } finally {
      setLoading(false)
    }
  }, [reelId, setReel, ssrReel])

  useEffect(() => {
    let cancelled = false

    async function runInitialLoad() {
      await Promise.resolve()
      if (!cancelled) {
        await load()
      }
    }

    void runInitialLoad()
    return () => {
      cancelled = true
    }
  }, [load])

  const retry = useCallback(async () => {
    await load()
  }, [load])

  return { reel, loading, error, retry }
}
